import dayjs from 'dayjs'
import { getAdminErrorMessage } from '../adminErrorModel'
import {
  LongRunningTask,
  LongRunningTaskDetails,
  LongRunningTaskRun,
  LongRunningTaskRunDetails,
  LongRunningTaskSummary,
  RunOutcome,
  TaskStatus,
} from './longRunningTasksApi'
import { getTaskTypeLabel } from './taskTypesModel'

export const defaultLongRunningTask = (): LongRunningTask => ({
  id: 1,
  taskType: 'job-refresh',
  parameters: '{}',
  status: 'Pending',
  currentRunId: null,
  resumeCursor: null,
  processedCount: 0,
  failedCount: 0,
  estimatedTotal: null,
  statusDetail: null,
  lastError: null,
  heartbeatAt: null,
  lastProgressAt: null,
  cancelRequestedAt: null,
  cancelRequestedBy: null,
  createdAt: '2026-10-20T12:00:00Z',
  createdBy: '00000000-0000-0000-0000-000000000000',
  startedAt: null,
  completedAt: null,
  updatedAt: '2026-10-20T12:00:00Z',
})

export const defaultLongRunningTaskSummary = (): LongRunningTaskSummary => ({
  task: defaultLongRunningTask(),
  isHeartbeatStale: false,
  isStalled: false,
  createdByEmail: null,
  cancelRequestedByEmail: null,
})

export const defaultLongRunningTaskRun = (): LongRunningTaskRun => ({
  id: 1,
  taskId: 1,
  ecsTaskArn: null,
  version: null,
  outcome: 'Running',
  exitCode: null,
  stoppedReason: null,
  launchedAt: '2026-10-20T12:00:00Z',
  launchedBy: '00000000-0000-0000-0000-000000000000',
  startedAt: null,
  endedAt: null,
})

export const TaskStatuses: TaskStatus[] = ['Pending', 'Running', 'Completed', 'Failed', 'Cancelled']

export type StatusFilter = 'all' | TaskStatus

export const statusFilterOptions: { label: string, value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  ...TaskStatuses.map(status => ({ label: status, value: status })),
]

export const toStatusParam = (filter: StatusFilter): TaskStatus | undefined => {
  if (filter === 'all') {
    return undefined
  }
  return filter
}

const taskStatusColors: Record<TaskStatus, string> = {
  Pending: 'default',
  Running: 'processing',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
}

export const taskStatusColor = (status: string): string => {
  return taskStatusColors[status as TaskStatus] ?? 'default'
}

const runOutcomeColors: Record<RunOutcome, string> = {
  Launching: 'default',
  Running: 'processing',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
  NotClaimed: 'warning',
  Lost: 'error',
}

export const runOutcomeColor = (outcome: string): string => {
  return runOutcomeColors[outcome as RunOutcome] ?? 'default'
}

export const isTaskActive = (status: string): boolean => {
  return status === 'Pending' || status === 'Running'
}

const SECOND = 1000
const MINUTE = 60 * SECOND

const autoRefreshTiers: { activeFor: number, interval: number }[] = [
  { activeFor: 2 * MINUTE, interval: 5 * SECOND },
  { activeFor: 30 * MINUTE, interval: 30 * SECOND },
]

const slowestAutoRefreshInterval = 2 * MINUTE

export const getTaskActiveSince = (task: LongRunningTask, runs: LongRunningTaskRunDetails[] = []): string => {
  if (task.status === 'Pending') {
    return task.updatedAt
  }
  const currentRun = runs.find(details => details.run.id === task.currentRunId)
  return currentRun?.run.startedAt ?? task.startedAt ?? task.createdAt
}

export const autoRefreshInterval = (status: string, activeSince: string, now: number): number | false => {
  if (!isTaskActive(status)) {
    return false
  }
  const activeFor = now - dayjs(activeSince).valueOf()
  return autoRefreshTiers.find(tier => activeFor < tier.activeFor)?.interval ?? slowestAutoRefreshInterval
}

export const taskAutoRefreshInterval = (details: LongRunningTaskDetails | undefined, now: number): number | false => {
  if (!details) {
    return false
  }
  const { task } = details.summary
  return autoRefreshInterval(task.status, getTaskActiveSince(task, details.runs), now)
}

export const listAutoRefreshInterval = (summaries: LongRunningTaskSummary[] | undefined, now: number): number | false => {
  const intervals = (summaries ?? [])
    .map(({ task }) => autoRefreshInterval(task.status, getTaskActiveSince(task), now))
    .filter((interval): interval is number => interval !== false)
  if (intervals.length === 0) {
    return false
  }
  return Math.min(...intervals)
}

export type RefreshSetting = 'auto' | 'off' | '5s' | '30s' | '1m' | '5m'

const fixedRefreshIntervals: Record<Exclude<RefreshSetting, 'auto' | 'off'>, number> = {
  '5s': 5 * SECOND,
  '30s': 30 * SECOND,
  '1m': MINUTE,
  '5m': 5 * MINUTE,
}

export const refreshSettingOptions: { value: RefreshSetting, label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'off', label: 'Off' },
  { value: '5s', label: 'Every 5s' },
  { value: '30s', label: 'Every 30s' },
  { value: '1m', label: 'Every 1m' },
  { value: '5m', label: 'Every 5m' },
]

export const resolveRefreshInterval = (setting: RefreshSetting, autoInterval: number | false): number | false => {
  if (setting === 'auto') {
    return autoInterval
  }
  if (setting === 'off') {
    return false
  }
  return fixedRefreshIntervals[setting]
}

export const formatInterval = (interval: number): string => {
  if (interval < MINUTE) {
    return `${Math.round(interval / SECOND)}s`
  }
  return `${Math.round(interval / MINUTE)}m`
}

export const formatRefreshSetting = (setting: RefreshSetting, autoInterval: number | false): string => {
  if (setting === 'auto') {
    return autoInterval === false ? 'Auto: stopped' : `Auto: ${formatInterval(autoInterval)}`
  }
  if (setting === 'off') {
    return 'Auto-refresh off'
  }
  return `Every ${formatInterval(fixedRefreshIntervals[setting])}`
}

export const formatUpdatedAt = (dataUpdatedAt: number): string | null => {
  if (dataUpdatedAt === 0) {
    return null
  }
  return `Updated ${dayjs(dataUpdatedAt).format('h:mm:ss A')}`
}

export const canCancel = (task: LongRunningTask): boolean => {
  return isTaskActive(task.status) && task.cancelRequestedAt === null
}

export const canResume = (task: LongRunningTask): boolean => {
  return task.status === 'Failed' || task.status === 'Cancelled'
}

const formatCount = (value: number): string => value.toLocaleString('en-US')

export const formatProgress = (task: LongRunningTask): string => {
  const total = task.estimatedTotal === null ? '?' : formatCount(task.estimatedTotal)
  return `${formatCount(task.processedCount)} / ${total}`
}

export const getProgressPercent = (task: LongRunningTask): number | null => {
  if (task.estimatedTotal === null || task.estimatedTotal <= 0) {
    return null
  }
  return Math.min(100, Math.floor((task.processedCount / task.estimatedTotal) * 100))
}

export type ProgressAppearance = {
  strokeColor: string
  status: 'active' | 'normal'
}

export const getProgressAppearance = (status: string): ProgressAppearance => {
  if (status === 'Running') {
    return { strokeColor: '#1677ff', status: 'active' }
  }
  return { strokeColor: '#bfbfbf', status: 'normal' }
}

export const formatDateTime =(value: string | null): string => {
  if (value === null) {
    return '—'
  }
  return dayjs(value).format('MMM D, YYYY h:mm:ss A')
}

export const getTaskLoadErrorMessage = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 404) {
    return 'Task not found.'
  }
  return getAdminErrorMessage(error, 'Failed to load the task.')
}

export type RunningTasksNotice = {
  type: 'info' | 'warning'
  message: string
}

const describeRunningCount = (count: number, noun: string): string => {
  return count === 1
    ? `1 ${noun} is already running.`
    : `${formatCount(count)} ${noun}s are already running.`
}

export const getRunningTasksNotices = (
  summaries: LongRunningTaskSummary[] | undefined,
  taskType: string,
): RunningTasksNotice[] => {
  const running = (summaries ?? []).filter(({ task }) => task.status === 'Running')
  const sameTypeCount = running.filter(({ task }) => task.taskType === taskType).length
  const notices: RunningTasksNotice[] = []
  if (running.length > 0) {
    notices.push({ type: 'info', message: describeRunningCount(running.length, 'task') })
  }
  if (sameTypeCount > 0) {
    notices.push({ type: 'warning', message: describeRunningCount(sameTypeCount, `${getTaskTypeLabel(taskType)} task`) })
  }
  return notices
}

export type CreateTaskErrors = {
  message: string
  errors: string[]
}

export const getCreateTaskErrors = (error: unknown): CreateTaskErrors => {
  const rawErrors = (error as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors
  const errors = Array.isArray(rawErrors)
    ? rawErrors.filter((item): item is string => typeof item === 'string')
    : []
  return {
    message: getAdminErrorMessage(error, 'Failed to start the task.'),
    errors,
  }
}
